const PROFILE_SHOP_PARAM = "ps";
const PROFILE_SHOP_PARAM_VALUE = "1";

import { getPriceObjFromAmount } from "@/features/pricing/components/IAIPrice/IAIPrice.vue";
import { FilterKey, ProfileShopFiltersState } from "@/features/profile-shop/composables/useProfileShopFilters";
import { FilterOption, IProfileShopFiltersResponse } from "@/features/profile-shop/services/api";
import type { AstroGlobal } from "astro";

export const SUGGEST_A_STYLE_URL = "https://airtable.com/appBPoGlW6N1mTkNe/pagDUMmy1XuwZLscE/form";
export const ASK_TO_BE_A_SELLER_URL = "https://airtable.com/appBPoGlW6N1mTkNe/pagVecnZ3mMJrxB6G/form";
export const TALENT_AI_PROFILES_URL = "https://imagen-ai.zendesk.com/hc/en-us/articles/18558566611741";
export const PROFILE_BASE_GUIDE_URL = "https://imagen-ai.zendesk.com/hc/en-us/articles/23595966001309";
export const SUPPORT_REQUEST_URL = "https://support.imagen-ai.com/hc/en-us/requests/new";

const checkProfileShopURL = (url: URL) => {
  return url.searchParams.get(PROFILE_SHOP_PARAM) === PROFILE_SHOP_PARAM_VALUE;
};

export const initURLWithProfileShopParam = (url: URL | string, isProfileShop: boolean) => {
  const _url = new URL(url);

  if (isProfileShop) {
    _url.searchParams.set(PROFILE_SHOP_PARAM, PROFILE_SHOP_PARAM_VALUE);
  } else {
    _url.searchParams.delete(PROFILE_SHOP_PARAM);
  }
  return _url;
};

export function isProfileShop() {
  return checkProfileShopURL(new URL(window?.location?.href ?? ""));
}

export const isProfileShopAstro = (Astro: AstroGlobal) => {
  return checkProfileShopURL(Astro.url);
};

export const getProfileUserFlow = (_isProfileShop: boolean = isProfileShop()) => {
  return _isProfileShop ? "profile-shop" : "purchase_profile";
};

export const MIN_PURCHASED_FOR_BADGE = 10;

export const decodeFiltersFromURL = (url: URL, filtersList: IProfileShopFiltersResponse): ProfileShopFiltersState => {
  const priceParam = url.searchParams
    .getAll("price")
    .map((price) => getPriceObjFromAmount(Number(price), filtersList.price.min.symbol));

  const price =
    priceParam.length === 2
      ? {
          min: priceParam[0],
          max: priceParam[1],
        }
      : filtersList.price;

  return {
    type: url.searchParams.getAll("type"),
    style: url.searchParams.getAll("style"),
    format: url.searchParams.getAll("format"),
    genres: url.searchParams.getAll("genres"),
    sort: url.searchParams.get("sort") ?? "popular",
    price,
  };
};

export const getHeightByIndex = (index: number) => {
  const heights = [18.75, 31.25, 15.625, 25, 17.5, 18.75, 16.25, 25, 15.625, 12.625];
  return `${heights[index % heights.length]}rem`;
};

export const sortFilterItems = (
  items: FilterOption[],
  filterName: FilterKey,
  includeFilterFn: (filterName: FilterKey, value: string) => boolean
): FilterOption[] => {
  return [...items].sort((a, b) => {
    const isItemAIncluded = includeFilterFn(filterName, a.value);
    const isItemBIncluded = includeFilterFn(filterName, b.value);
    if (isItemAIncluded !== isItemBIncluded) {
      return isItemAIncluded ? -1 : 1;
    }
    return a.label.localeCompare(b.label);
  });
};

/**
 * Groups filter items into two groups:
 * 1. Primary group: All selected items + up to `MAX_PRIMARY_GROUP_ITEMS` total items (including selected)
 * 2. Secondary group: The rest of the items
 * Both groups are sorted alphabetically within themselves
 */
const MAX_PRIMARY_GROUP_ITEMS = 10;
export const groupFilterItems = (
  items: FilterOption[],
  filterName: FilterKey,
  includeFilterFn: (filterName: FilterKey, value: string) => boolean
) => {
  const selectedItems = items.filter((item) => includeFilterFn(filterName, item.value));
  const nonSelectedItems = items.filter((item) => !includeFilterFn(filterName, item.value));

  selectedItems.sort((a, b) => a.label.localeCompare(b.label));
  nonSelectedItems.sort((a, b) => a.label.localeCompare(b.label));

  const remainingSlots = Math.max(0, MAX_PRIMARY_GROUP_ITEMS - selectedItems.length);
  const primaryGroupNonSelected = nonSelectedItems.slice(0, remainingSlots);
  const secondaryGroupItems = nonSelectedItems.slice(remainingSlots);

  return {
    items: [...selectedItems, ...primaryGroupNonSelected],
    restItems: secondaryGroupItems,
  };
};

export const PROFILE_LINK_SOURCES = {
  main_page: "main_page",
  banner: "banner",
  empty_state: "empty_state",
  profile_page_similar: "profile_page_similar",
};

export type ProfileLinkSource = keyof typeof PROFILE_LINK_SOURCES;
